import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, uploadFile } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, StatusBadge, Modal, Textarea, FileUpload, toast, Spinner, EmptyState, Badge } from '../components/UI'
import { BookOpen, MapPin, Calendar, CheckCircle, XCircle, Upload, Image, Eye, MessageSquare, ArrowLeft } from 'lucide-react'
import { format } from 'date-fns'

// ── Status timeline component ─────────────────────────────────
function StatusTimeline({ status }) {
  const steps = [
    { key: 'inquiry', label: 'Inquiry Sent' },
    { key: 'approved', label: 'Approved' },
    { key: 'artwork_pending', label: 'Artwork Submitted' },
    { key: 'artwork_approved', label: 'Artwork Approved' },
    { key: 'active', label: 'Campaign Live' },
    { key: 'completed', label: 'Completed' },
  ]
  const cancelledOrRejected = ['rejected', 'cancelled', 'artwork_rejected'].includes(status)
  const currentIndex = steps.findIndex(s => s.key === status)

  return (
    <div style={{ padding: '16px 0' }}>
      {cancelledOrRejected ? (
        <div style={{ display: 'flex', gap: 8, background: '#FFF1F2', borderRadius: 8, padding: '12px 16px', color: '#BE123C', fontWeight: 600, alignItems: 'center' }}>
          <XCircle size={16} />
          {status === 'rejected' ? 'Booking was rejected by the media owner.' : status === 'artwork_rejected' ? 'Artwork was rejected. Please resubmit.' : 'Booking was cancelled.'}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 8 }}>
          {steps.map((step, i) => {
            const done = i <= currentIndex
            const active = i === currentIndex
            return (
              <React.Fragment key={step.key}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, minWidth: 80 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: done ? 'var(--tech)' : 'var(--gray-200)',
                    color: done ? 'white' : 'var(--gray-400)',
                    fontWeight: 700, fontSize: '0.8rem',
                    border: active ? '3px solid var(--tech)' : 'none',
                    boxSizing: 'border-box', transition: 'all 0.3s',
                    boxShadow: active ? '0 0 0 4px rgba(48,102,190,0.2)' : 'none'
                  }}>
                    {done ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <div style={{ fontSize: '0.7rem', marginTop: 6, fontWeight: active ? 700 : 400, color: done ? 'var(--tech)' : 'var(--gray-400)', textAlign: 'center', lineHeight: 1.3 }}>
                    {step.label}
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div style={{
                    flex: 1, height: 2, background: i < currentIndex ? 'var(--tech)' : 'var(--gray-200)',
                    minWidth: 20, transition: 'background 0.3s', marginBottom: 18
                  }} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Bookings list page ───────────────────────────────────────
export function BookingsPage() {
  const { profile, isMediaOwner } = useAuth()
  const navigate = useNavigate()
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => { load() }, [filter])

  async function load() {
    let q = supabase
      .from('bookings')
      .select('*, ad_spaces(title, area, type, photos)')
      .order('created_at', { ascending: false })

    if (isMediaOwner) q = q.eq('owner_id', profile.id)
    else q = q.eq('advertiser_id', profile.id)

    if (filter !== 'all') q = q.eq('status', filter)

    const { data } = await q
    setBookings(data || [])
    setLoading(false)
  }

  const statusFilters = isMediaOwner
    ? [['all', 'All'], ['inquiry', 'New Requests'], ['approved', 'Approved'], ['active', 'Active'], ['completed', 'Completed']]
    : [['all', 'All'], ['inquiry', 'Pending'], ['approved', 'Approved'], ['active', 'Active'], ['completed', 'Past']]

  return (
    <DashboardLayout title="Bookings">
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--gray-100)', borderRadius: 8, padding: 3, marginBottom: 24, alignSelf: 'flex-start', width: 'fit-content' }}>
        {statusFilters.map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: filter === val ? 'white' : 'transparent',
            color: filter === val ? 'var(--navy)' : 'var(--gray-500)',
            fontWeight: filter === val ? 600 : 400, fontSize: '0.85rem',
            boxShadow: filter === val ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s'
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
      ) : bookings.length === 0 ? (
        <EmptyState icon={<BookOpen size={36} />} title="No bookings found" description="Bookings will appear here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bookings.map(b => (
            <div key={b.id} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}
              onClick={() => navigate(`/bookings/${b.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {/* Thumbnail */}
                <div style={{ width: 100, height: 80, flexShrink: 0, background: 'var(--gray-200)', overflow: 'hidden' }}>
                  <img
                    src={b.ad_spaces?.photos?.[0] || 'https://images.unsplash.com/photo-1617550523898-b2b67b3d5b16?w=200&q=80'}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                {/* Content */}
                <div style={{ flex: 1, padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{b.ad_spaces?.title || 'Ad Space'}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} /> {b.ad_spaces?.area}
                    </div>
                    {b.brand_name && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 2 }}>Campaign: {b.brand_name}</div>}
                  </div>
                  <div style={{ minWidth: 120 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Period</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      {b.start_date ? format(new Date(b.start_date), 'dd MMM') : '—'} – {b.end_date ? format(new Date(b.end_date), 'dd MMM yy') : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      {isMediaOwner ? 'Payout' : 'Total'}
                    </div>
                    <div style={{ fontWeight: 800, color: 'var(--navy)' }}>
                      {(isMediaOwner ? b.owner_payout : b.total_amount)?.toLocaleString()} AED
                    </div>
                  </div>
                  <StatusBadge status={b.status} />
                  <div style={{ color: 'var(--gray-400)' }}>→</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  )
}

// ── Single booking detail page ───────────────────────────────
export function BookingDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, isMediaOwner } = useAuth()
  const [booking, setBooking] = useState(null)
  const [space, setSpace] = useState(null)
  const [loading, setLoading] = useState(true)
  const [artworkFile, setArtworkFile] = useState(null)
  const [artworkUploading, setArtworkUploading] = useState(false)
  const [proofFile, setProofFile] = useState(null)
  const [proofUploading, setProofUploading] = useState(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => { load() }, [id])

  async function load() {
    const { data } = await supabase.from('bookings').select('*').eq('id', id).single()
    if (data) {
      setBooking(data)
      const { data: spaceData } = await supabase.from('ad_spaces').select('*').eq('id', data.ad_space_id).single()
      setSpace(spaceData)
    }
    setLoading(false)
  }

  async function updateStatus(status, extra = {}) {
    setActionLoading(true)
    const { error } = await supabase.from('bookings').update({ status, ...extra }).eq('id', id)
    if (error) { toast.error('Update failed'); setActionLoading(false); return }
    setBooking(prev => ({ ...prev, status, ...extra }))
    toast.success(`Booking ${status.replace('_', ' ')}`)
    setActionLoading(false)
  }

  async function uploadArtwork() {
    if (!artworkFile) return
    setArtworkUploading(true)
    try {
      const path = `${booking.id}/${artworkFile.name}`
      const url = await uploadFile('artwork-uploads', path, artworkFile)
      await supabase.from('bookings').update({ artwork_url: url, status: 'artwork_pending' }).eq('id', id)
      setBooking(prev => ({ ...prev, artwork_url: url, status: 'artwork_pending' }))
      toast.success('Artwork submitted for review!')
    } catch (e) {
      toast.error('Upload failed')
    } finally {
      setArtworkUploading(false)
    }
  }

  async function uploadProof() {
    if (!proofFile) return
    setProofUploading(true)
    try {
      const path = `${booking.id}/proof-${proofFile.name}`
      const url = await uploadFile('proof-of-posting', path, proofFile)
      await supabase.from('bookings').update({
        proof_photo_url: url, proof_uploaded_at: new Date().toISOString(), status: 'active'
      }).eq('id', id)
      setBooking(prev => ({ ...prev, proof_photo_url: url, status: 'active' }))
      toast.success('Proof of posting uploaded! Campaign is now active.')
    } catch (e) {
      toast.error('Upload failed')
    } finally {
      setProofUploading(false)
    }
  }

  if (loading) return (
    <DashboardLayout><div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={40} /></div></DashboardLayout>
  )

  if (!booking) return <DashboardLayout><p style={{ padding: 40 }}>Booking not found</p></DashboardLayout>

  return (
    <DashboardLayout title="Booking Details">
      <div style={{ marginBottom: 20 }}>
        <Button variant="ghost" icon={<ArrowLeft size={16} />} onClick={() => navigate('/bookings')}>Back to Bookings</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Header */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginBottom: 4, fontFamily: 'monospace' }}>#{id.slice(0, 8).toUpperCase()}</div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.3rem' }}>{space?.title}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--gray-500)', fontSize: '0.88rem', marginTop: 4 }}>
                  <MapPin size={13} /> {space?.area}, Dubai
                </div>
              </div>
              <StatusBadge status={booking.status} />
            </div>

            <StatusTimeline status={booking.status} />
          </div>

          {/* Campaign info */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 16 }}>Campaign Details</h3>
            <div className="grid-2" style={{ gap: 14 }}>
              {[
                ['Campaign', booking.campaign_name || '—'],
                ['Brand', booking.brand_name || '—'],
                ['Start Date', booking.start_date ? format(new Date(booking.start_date), 'dd MMMM yyyy') : '—'],
                ['End Date', booking.end_date ? format(new Date(booking.end_date), 'dd MMMM yyyy') : '—'],
                ['Duration', `${booking.total_days || '—'} days`],
                ['Payment', <Badge key="p" variant={booking.payment_status === 'paid' ? 'success' : 'warning'}>{booking.payment_status}</Badge>],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontWeight: 600 }}>{value}</div>
                </div>
              ))}
            </div>
            {booking.advertiser_notes && (
              <div style={{ marginTop: 14, padding: 12, background: 'var(--gray-50)', borderRadius: 8, fontSize: '0.88rem', color: 'var(--gray-600)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes from Advertiser</div>
                {booking.advertiser_notes}
              </div>
            )}
          </div>

          {/* Artwork section */}
          {(booking.status === 'approved' || booking.status === 'artwork_pending' || booking.status === 'artwork_approved' || booking.status === 'artwork_rejected') && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 14 }}>
                <Image size={18} style={{ display: 'inline', marginRight: 8 }} />
                Artwork
              </h3>

              {/* Show existing artwork */}
              {booking.artwork_url && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>Submitted Artwork</div>
                  <a href={booking.artwork_url} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: 'var(--gray-50)', padding: '8px 14px', borderRadius: 8, fontSize: '0.85rem', color: 'var(--tech)', fontWeight: 500, border: '1px solid var(--gray-200)' }}>
                    <Eye size={14} /> View Artwork File
                  </a>
                </div>
              )}

              {/* Advertiser: upload artwork if approved and no artwork yet */}
              {!isMediaOwner && booking.status === 'approved' && !booking.artwork_url && (
                <div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', marginBottom: 12 }}>
                    Your booking is approved! Please upload your artwork file to proceed.
                  </p>
                  <FileUpload label="Upload Artwork" accept=".jpg,.jpeg,.png,.pdf,.mp4,.ai,.eps"
                    hint={`Required size: ${space?.width_meters || '?'}m × ${space?.height_meters || '?'}m. Accepted: JPG, PNG, PDF, AI, EPS, MP4`}
                    onFiles={(files) => setArtworkFile(files[0])} />
                  {artworkFile && (
                    <Button onClick={uploadArtwork} loading={artworkUploading} icon={<Upload size={16} />} style={{ marginTop: 12 }}>
                      Submit Artwork for Review
                    </Button>
                  )}
                </div>
              )}

              {/* Media owner: approve/reject artwork */}
              {isMediaOwner && booking.status === 'artwork_pending' && (
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <Button icon={<CheckCircle size={16} />} loading={actionLoading}
                    onClick={() => updateStatus('artwork_approved')}>
                    Approve Artwork
                  </Button>
                  <Button variant="danger" icon={<XCircle size={16} />}
                    onClick={() => setRejectModal(true)}>
                    Reject Artwork
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Proof of posting */}
          {isMediaOwner && (booking.status === 'artwork_approved' || booking.status === 'active') && !booking.proof_photo_url && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 12 }}>Proof of Posting</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', marginBottom: 14 }}>
                Upload a photo of the installed ad to confirm it's live and unlock the advertiser payment.
              </p>
              <FileUpload label="Upload Proof Photo" accept="image/*"
                hint="Photo should clearly show the ad in place on the street/screen."
                onFiles={(files) => setProofFile(files[0])} preview />
              {proofFile && (
                <Button onClick={uploadProof} loading={proofUploading} icon={<Upload size={16} />} style={{ marginTop: 12 }}>
                  Upload Proof of Posting
                </Button>
              )}
            </div>
          )}

          {booking.proof_photo_url && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 12 }}>✅ Proof of Posting</h3>
              <a href={booking.proof_photo_url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: '#D1FAE5', padding: '8px 14px', borderRadius: 8, fontSize: '0.85rem', color: '#065F46', fontWeight: 500 }}>
                <Eye size={14} /> View Live Ad Photo
              </a>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 80 }}>
          {/* Price breakdown */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 16 }}>Price Breakdown</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['Base Price', `${booking.base_price?.toLocaleString()} AED`],
                ['AdsLocate Fee', `${booking.commission_amount?.toLocaleString()} AED`],
                booking.production_fee > 0 ? ['Production Fee', `${booking.production_fee?.toLocaleString()} AED`] : null,
              ].filter(Boolean).map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: 'var(--gray-500)' }}>
                  <span>{l}</span><span>{v}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: 'var(--navy)' }}>
                <span>Total to Advertiser</span><span>{booking.total_amount?.toLocaleString()} AED</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--success)', fontSize: '0.9rem' }}>
                <span>Owner Payout</span><span>{booking.owner_payout?.toLocaleString()} AED</span>
              </div>
            </div>
          </div>

          {/* Actions for media owner */}
          {isMediaOwner && booking.status === 'inquiry' && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 14 }}>Review Request</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: 14 }}>
                {booking.brand_name} is requesting your space for {booking.total_days} days.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button fullWidth icon={<CheckCircle size={16} />} loading={actionLoading}
                  onClick={() => updateStatus('approved')}>
                  Approve Booking
                </Button>
                <Button fullWidth variant="danger" icon={<XCircle size={16} />}
                  onClick={() => setRejectModal(true)}>
                  Reject Booking
                </Button>
              </div>
            </div>
          )}

          {/* Mark complete */}
          {isMediaOwner && booking.status === 'active' && (
            <Button fullWidth icon={<CheckCircle size={16} />} loading={actionLoading}
              onClick={() => updateStatus('completed')}>
              Mark as Completed
            </Button>
          )}

          {/* Cancel for advertiser */}
          {!isMediaOwner && ['inquiry', 'approved'].includes(booking.status) && (
            <Button fullWidth variant="outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={() => updateStatus('cancelled')}>
              Cancel Booking
            </Button>
          )}
        </div>
      </div>

      {/* Reject modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)} title="Provide Rejection Reason"
        footer={
          <>
            <Button variant="outline" onClick={() => setRejectModal(false)}>Cancel</Button>
            <Button variant="danger" loading={actionLoading} onClick={() => {
              updateStatus(booking.status === 'artwork_pending' ? 'artwork_rejected' : 'rejected', { rejection_reason: rejectReason })
              setRejectModal(false)
            }}>
              Confirm Rejection
            </Button>
          </>
        }
      >
        <Textarea label="Reason (optional)" placeholder="Tell the advertiser why you're rejecting this request..."
          value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4} />
      </Modal>
    </DashboardLayout>
  )
}
